import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Typography, 
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS } from '../../../../config/api';

// Custom event name for application settings type updates
const APP_SETTINGS_TYPE_UPDATED_EVENT = 'app_settings_type_updated';

// Input types equivalent to the Laravel INPUT_TYPES constant
const INPUT_TYPES = {
  text: 'Text',
  textarea: 'Textarea',
  number: 'Number',
  email: 'Email',
  password: 'Password',
  select: 'Select',
  radio: 'Radio',
  checkbox: 'Checkbox',
  date: 'Date',
  file: 'File'
};

const EditApplicationSettings = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the ID from URL
  const [formData, setFormData] = useState({
    field_name: '',
    slug: '',
    input_type: '',
    options: '',
    application_setting_category_id: '',
    application_setting_type_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);

  // Function to convert string to slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        // Fetch categories
        const categoriesEndpoint = API_ENDPOINTS.APPLICATION_CATEGORIES?.BASE || 
                                  `${API_ENDPOINTS.APPLICATION_TYPES.BASE.replace('types', 'categories')}`;
        
        const categoriesResponse = await axios.get(categoriesEndpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setCategories(categoriesResponse.data.data || []);

        // Fetch types
        const typesResponse = await axios.get(API_ENDPOINTS.APPLICATION_TYPES.BASE, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setTypes(typesResponse.data.data || []);

        // Fetch the specific application setting
        const settingEndpoint = API_ENDPOINTS.APPLICATION_SETTINGS?.EDIT?.(id) || 
                               `${API_ENDPOINTS.APPLICATION_SETTINGS?.BASE || 
                                 API_ENDPOINTS.APPLICATION_TYPES.BASE.replace('types', 'settings')}/${id}`;
        
        const settingResponse = await axios.get(settingEndpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const setting = settingResponse.data.data;
        
        // Check if this input type should show options
        const shouldShowOptions = ['select', 'radio', 'checkbox'].includes(setting.input_type);
        setShowOptions(shouldShowOptions);

        // Set form data
        setFormData({
          field_name: setting.field_name || '',
          slug: setting.slug || '',
          input_type: setting.input_type || '',
          options: setting.options || '',
          application_setting_category_id: setting.application_setting_category_id || '',
          application_setting_type_id: setting.application_setting_type_id || ''
        });

        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch application setting data. ' + (error.response?.data?.message || ''));
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, navigate]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for input_type to show/hide options
    if (name === 'input_type') {
      const shouldShowOptions = ['select', 'radio', 'checkbox'].includes(value);
      setShowOptions(shouldShowOptions);
    }

    // Generate slug automatically from field_name
    if (name === 'field_name') {
      setFormData({
        ...formData,
        [name]: value,
        slug: convertToSlug(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      // Validate required fields
      if (!formData.field_name || !formData.input_type || !formData.application_setting_type_id) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Validate options for select, radio, checkbox
      if (['select', 'radio', 'checkbox'].includes(formData.input_type) && !formData.options) {
        setError('Options are required for the selected input type');
        setLoading(false);
        return;
      }

      // Endpoint for updating
      const endpoint = API_ENDPOINTS.APPLICATION_SETTINGS?.EDIT?.(id) || 
                      `${API_ENDPOINTS.APPLICATION_SETTINGS?.BASE || 
                        API_ENDPOINTS.APPLICATION_TYPES.BASE.replace('types', 'settings')}/${id}`;
                    
      // console.log('Updating setting at:', endpoint);
      // console.log('Update data:', formData);

      // Send request to update application setting
      const response = await axios.put(endpoint, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(true);
        
        // Dispatch custom event to trigger sidebar refresh
        window.dispatchEvent(new CustomEvent(APP_SETTINGS_TYPE_UPDATED_EVENT));
        
        setTimeout(() => {
          navigate('/application-settings');
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating application setting:', error);
      
      // More detailed error logging
      if (error.response) {
        console.error('Server response data:', error.response.data);
        console.error('Server response status:', error.response.status);
        
        // Display more informative error message
        if (error.response.data && error.response.data.errors) {
          // Laravel validation errors
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          setError(`Validation error: ${errorMessages}`);
        } else if (error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else {
          setError(`Server error (${error.response.status}). Please check server logs.`);
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
        setError('No response from server. Please check if the API is running.');
      } else {
        console.error('Error setting up request:', error.message);
        setError(`Request setup error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {/* <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/application-settings')} 
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Typography variant="h5">
              Edit Application Setting
            </Typography> */}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Application setting updated successfully!
            </Alert>
          )}

          {fetchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Field Name Field */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Field Name"
                    name="field_name"
                    value={formData.field_name}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                {/* Slug Field */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Slug"
                    name="slug"
                    value={formData.slug}
                    InputProps={{
                      readOnly: true,
                    }}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                {/* Input Type Field */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth required>
                    <InputLabel id="input-type-label">Select Input Type</InputLabel>
                    <Select
                      labelId="input-type-label"
                      id="input_type"
                      name="input_type"
                      value={formData.input_type}
                      onChange={handleChange}
                      label="Select Input Type"
                    >
                      <MenuItem value="">Select Input Type</MenuItem>
                      {Object.entries(INPUT_TYPES).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Options Field - Only shown for select, radio, checkbox */}
                {showOptions && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Options"
                      name="options"
                      value={formData.options}
                      onChange={handleChange}
                      required
                      multiline
                      rows={4}
                      helperText="Enter options, one per line"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}
                
                {/* Category Field */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel id="category-label">Select Category</InputLabel>
                    <Select
                      labelId="category-label"
                      id="application_setting_category_id"
                      name="application_setting_category_id"
                      value={formData.application_setting_category_id}
                      onChange={handleChange}
                      label="Select Category"
                    >
                      <MenuItem value="">Select Category</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
\                  </FormControl>
                </Grid>
                
                {/* Type Field */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth required>
                    <InputLabel id="type-label">Select Type</InputLabel>
                    <Select
                      labelId="type-label"
                      id="application_setting_type_id"
                      name="application_setting_type_id"
                      value={formData.application_setting_type_id}
                      onChange={handleChange}
                      label="Select Type"
                    >
                      <MenuItem value="">Select Type</MenuItem>
                      {types.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.type}
                        </MenuItem>
                      ))}
                    </Select>

                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Update Application Setting'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate('/application-settings')}
                      sx={{ ml: 2 }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EditApplicationSettings;
